import React from 'react'
import { InvoiceFeesDto } from '../../../api/dto/generated-invoice.dto'
import {
  SummaryGroupLabel,
  SummaryLines,
  SummaryNote,
  SummaryRow,
  SummaryTotalCard,
  SummaryWrap,
} from './InvoiceGenerationView.styles'
import {
  formatAmount,
  formatFeeCalc,
  getBrokerFeeLabel,
  SURECO_PLATFORM_FEE_LABEL,
  THE_CASON_GROUP_FEE_LABEL,
} from './utils'

interface InvoiceSummaryTabProps {
  /** Funded-month premiums (the Health Plans tab), in dollars. */
  planCosts: number
  /**
   * Net of the Adjustments tab in dollars; negative when credits exceed
   * charges.
   */
  adjustments: number
  /** Authoritative invoice total (the persisted charged amount). */
  totalDue: number
  fees: InvoiceFeesDto | null
  /**
   * Whether the drawer shows the Adjustments tab. Users without US data access
   * don't get it (EMS-10939), so the Adjustments line doesn't point them there.
   */
  hasAdjustmentsTab?: boolean
}

// Sub-cent residue is rounding noise, not a real line.
const AMOUNT_EPSILON = 0.005

// Presentational Invoice Summary tab. `totalDue` is the invoice's persisted
// charged amount (the single source of truth); plan costs, adjustments and
// fees are shown as its breakdown, each sourced from the invoice detail rather
// than derived here. Anything the three do not explain — e.g. an invoice
// generated before Medicare left the total — is surfaced as an explicit Other
// line so the breakdown always reconciles to the real total.
const InvoiceSummaryTab: React.FC<InvoiceSummaryTabProps> = ({
  planCosts,
  adjustments,
  totalDue,
  fees,
  hasAdjustmentsTab = true,
}) => {
  const feesTotal = fees
    ? (fees.surecoFeeCents + fees.brokerFeeCents + fees.tcgFeeCents) / 100
    : 0
  const showTcgFee = fees !== null && fees.tcgFeeCents !== 0
  const hasAdjustments = Math.abs(adjustments) >= AMOUNT_EPSILON
  const other = totalDue - planCosts - adjustments - feesTotal
  const hasOther = Math.abs(other) >= AMOUNT_EPSILON

  return (
    <SummaryWrap>
      <SummaryLines role="table" aria-label="Invoice summary breakdown">
        <SummaryRow role="row">
          <span className="summary-label" role="rowheader">
            Plan Costs
          </span>
          <span className="summary-rate" aria-hidden="true" />
          <span className="summary-sym" aria-hidden="true">
            $
          </span>
          <span className="summary-val" role="cell">
            {formatAmount(planCosts)}
          </span>
        </SummaryRow>
        {fees ? (
          <>
            <div role="row">
              <SummaryGroupLabel role="rowheader">
                Fees (× {fees.billableEmployeeCount} billable)
              </SummaryGroupLabel>
            </div>
            <SummaryRow role="row">
              <span className="summary-label" role="rowheader">
                {SURECO_PLATFORM_FEE_LABEL}
              </span>
              <span className="summary-rate" role="cell">
                {formatFeeCalc(
                  fees.surecoPepmCents,
                  fees.billableEmployeeCount
                )}
              </span>
              <span className="summary-sym" aria-hidden="true">
                $
              </span>
              <span className="summary-val" role="cell">
                {formatAmount(fees.surecoFeeCents / 100)}
              </span>
            </SummaryRow>
            <SummaryRow role="row">
              <span className="summary-label" role="rowheader">
                {getBrokerFeeLabel(fees.brokerName)}
              </span>
              <span className="summary-rate" role="cell">
                {formatFeeCalc(
                  fees.brokerPepmCents,
                  fees.billableEmployeeCount
                )}
              </span>
              <span className="summary-sym" aria-hidden="true">
                $
              </span>
              <span className="summary-val" role="cell">
                {formatAmount(fees.brokerFeeCents / 100)}
              </span>
            </SummaryRow>
            {showTcgFee ? (
              <SummaryRow role="row">
                <span className="summary-label" role="rowheader">
                  {THE_CASON_GROUP_FEE_LABEL}
                </span>
                <span className="summary-rate" role="cell">
                  {formatFeeCalc(fees.tcgPepmCents, fees.billableEmployeeCount)}
                </span>
                <span className="summary-sym" aria-hidden="true">
                  $
                </span>
                <span className="summary-val" role="cell">
                  {formatAmount(fees.tcgFeeCents / 100)}
                </span>
              </SummaryRow>
            ) : null}
          </>
        ) : null}
        {hasAdjustments ? (
          <SummaryRow role="row" $credit={adjustments < 0}>
            <span className="summary-label" role="rowheader">
              Adjustments
              <small>
                {hasAdjustmentsTab
                  ? 'Credits and corrections — see the Adjustments tab'
                  : 'Credits and corrections'}
              </small>
            </span>
            <span className="summary-rate" aria-hidden="true" />
            <span className="summary-sym" aria-hidden="true">
              $
            </span>
            <span className="summary-val" role="cell">
              {formatAmount(adjustments)}
            </span>
          </SummaryRow>
        ) : null}
        {hasOther ? (
          <SummaryRow role="row" $credit={other < 0}>
            <span className="summary-label" role="rowheader">
              Other
              <small>Not in plan costs, adjustments or fees</small>
            </span>
            <span className="summary-rate" aria-hidden="true" />
            <span className="summary-sym" aria-hidden="true">
              $
            </span>
            <span className="summary-val" role="cell">
              {formatAmount(other)}
            </span>
          </SummaryRow>
        ) : null}
      </SummaryLines>
      <SummaryNote>
        Total Due is the net amount charged for this invoice — funded-month plan
        costs, adjustments and fees.
      </SummaryNote>
      <SummaryTotalCard>
        <span className="summary-total-label">Total Due</span>
        <span />
        <span className="summary-total-sym">$</span>
        <span className="summary-total-val">{formatAmount(totalDue)}</span>
      </SummaryTotalCard>
    </SummaryWrap>
  )
}

export { InvoiceSummaryTab }
